/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoHowTos, IoHowToSteps } from '@cloudio-saas/datasource-types';
import { emptyFunction } from '@cloudio-saas/statex';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Platform } from 'componentregistry';
import SlateRTE from 'components/RichTextEditor/Slate';
import { getUrl } from 'config/config';
import Icon, { parseIconText } from 'icons/Icon';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'utils/isEmpty';
import NoDataFound from 'utils/NoDataFound';
import { getURLQueryStringToPost } from 'utils/url';
import WaveDots from 'utils/WaveDots';
import HowToHeader from './HowToHeader';
import { Tags } from './HowToHome';
import HowToInfo from './HowToInfo';
import Step from './HowToStep';
import { NavigationStep } from './NavigationStep';

export function HowToInternal({
  appUid,
  howToUid,
  followOn = false,
  minimized = false,
}: {
  appUid: string;
  howToUid: string;
  platform: Platform;
  followOn?: boolean;
  minimized?: boolean;
}) {
  const [loading, setLoading] = useState(true);
  const [howTo, setHowTo] = useState<DBRow<IoHowTos> | undefined>();
  const [steps, setSteps] = useState<DBRow<IoHowToSteps>[] | undefined>();
  useEffect(() => {
    if (isEmpty(appUid) || isEmpty(howToUid)) return;
    async function query() {
      try {
        const response = await fetch(
          `${getUrl()}public?${getURLQueryStringToPost()}`,
          {
            method: 'POST',
            headers: {
              'X-Application': appUid,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              a: {
                ds: 'IoHowTos',
                query: { limit: 1, data: { howToUid } },
              },
            }),
          },
        );
        const json = await response.json();
        if (json.status === 'OK') {
          setHowTo(json.data.a.data[0]);
        }

        const sresponse = await fetch(
          `${getUrl()}public?${getURLQueryStringToPost()}`,
          {
            method: 'POST',
            headers: {
              'X-Application': appUid,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              a: {
                ds: 'IoHowToSteps',
                query: { limit: 240, data: { howToUid }, sort: { stepNo: 1 } },
              },
            }),
          },
        );
        const sjson = await sresponse.json();
        if (sjson.status === 'OK') {
          setSteps(sjson.data.a.data);
        }
      } catch (e) {
        if (console) console.error(e);
      } finally {
        setLoading(false);
      }
    }
    query();
  }, [appUid, howToUid]);
  const theme = useTheme();

  if (loading) {
    if (!followOn) {
      window.document.title = 'Loading...';
    }
    return (
      <Box sx={{ display: 'flex', flex: 1, height: '100vh' }}>
        <HowToHeader appUid={appUid} />
        <WaveDots active />
      </Box>
    );
  }

  if (!howTo) {
    window.document.title = 'No data found...';
    return (
      <>
        <HowToHeader appUid={appUid} />
        <NoDataFound
          message={
            <>
              <Typography>
                How to "{howToUid}" is either invalid or may have been removed!
              </Typography>
              <IconButton
                color="primary"
                onClick={() => {
                  window.location.href = `#/how-to/${appUid}`;
                }}
              >
                <Icon color="primary" iconName="home" />
              </IconButton>
            </>
          }
        />
      </>
    );
  }

  if (followOn && minimized) {
    return null;
  }

  if (!followOn) {
    window.document.title = howTo.howToTitle;
  }

  const { iconName = 'desktop', iconType } = parseIconText(howTo.icon) ?? {};

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        backgroundColor: 'rgb(248 250 252)',
      }}
    >
      {!followOn ? <HowToHeader howTo={howTo} appUid={appUid} /> : null}
      <Box sx={{ flex: 1, mt: followOn ? 2 : '90px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              boxShadow:
                '0 0 #0000,0 0 #0000, 0 10px 15px -3px rgba(15,23,42,.1),0 4px 6px -4px rgba(15,23,42,.1)',
              borderRadius: '9999px',
              width: '88px',
              height: '88px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: `linear-gradient(to bottom,${
                howTo.iconColor ?? theme.palette.primary.main
              },#fff)`,
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                width: '4rem',
                height: '4rem',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon
                width={32}
                height={32}
                iconName={iconName}
                iconType={iconType}
                color={howTo.iconColor || theme.palette.primary.main}
              />
            </Box>
          </Box>
          <Typography
            ml={2}
            mr={2}
            variant={followOn ? 'h5' : 'h4'}
            textAlign="center"
          >
            {howTo.howToTitle}
          </Typography>

          <HowToInfo howTo={howTo} followOn={followOn} />

          {!followOn && howTo.md?.tags ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Tags appUid={appUid} tags={howTo.md.tags} />
            </Box>
          ) : null}

          {howTo.howToDescription ? (
            <Box
              sx={{
                '& .slate': {
                  bgcolor: 'transparent',
                  padding: 2,
                },
              }}
            >
              <SlateRTE
                className="slate"
                width={followOn ? '440px' : '720px'}
                height="auto"
                value={howTo.howToDescription}
                onChange={emptyFunction}
                readOnly
                autoFocus={false}
              />
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            mb: followOn ? 0 : 8,
            p: 2,
            flex: 1,
          }}
        >
          {followOn ? null : <NavigationStep howTo={howTo} />}
          {steps?.map((step, i) => (
            <Step
              key={step.stepUid}
              step={step}
              index={i + (followOn ? 1 : 2)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default function HowTo({ platform }: { platform: Platform }) {
  const { appUid, howToUid } = useParams() as {
    appUid: string;
    howToUid: string;
  };

  return (
    <HowToInternal appUid={appUid} howToUid={howToUid} platform={platform} />
  );
}
